import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMultimodalClient } from '../components/audio_video/use-multimodal-client';
import { storeChatHistory } from '../chatHistory';

const VoiceContext = createContext();

export const VoiceProvider = ({ children, userId, chatSessionId }) => {
  const multimodal = useMultimodalClient(userId, chatSessionId);
  const [showVoicePopup, setShowVoicePopup] = useState(false);
  const [voiceAnimationState, setVoiceAnimationState] = useState('idle');
  const [voicePosition, setVoicePosition] = useState({ x: window.innerWidth - 350, y: window.innerHeight - 250 });
  const [voiceStatus, setVoiceStatus] = useState('idle'); // 'idle' | 'listening' | 'calling_tool' | 'responding'

  // Update animation state based on multimodal state
  useEffect(() => {
    if (multimodal.isModelSpeaking) {
      if (multimodal.isModelFunctionCall) {
        setVoiceAnimationState("tool-calling");
        setVoiceStatus('calling_tool');
      } else {
        setVoiceAnimationState("ai-talking");
        setVoiceStatus('responding');
      }
    } else {
      if (multimodal.isModelFunctionCall) {
        setVoiceAnimationState("tool-calling");
        setVoiceStatus('calling_tool');
      } else if (multimodal.isConnected) {
        setVoiceAnimationState("listening");
        setVoiceStatus('listening');
      } else {
        setVoiceAnimationState("idle");
        setVoiceStatus('idle');
      }
    }
  }, [multimodal.isModelSpeaking, multimodal.isModelFunctionCall, multimodal.isConnected]);

  // Handle voice responses (text from model)
  useEffect(() => {
    if (multimodal.onTextReceived && Array.isArray(multimodal.onTextReceived)) {
      const modelResponse = multimodal.onTextReceived.join("");
      if (modelResponse.trim()) {
        storeChatHistory(
          chatSessionId,
          modelResponse,
          "text",
          false,
          null,
          null,
          userId
        );
      }
    }
  }, [multimodal.onTextReceived, userId, chatSessionId]);

  // Handle user speech transcription
  useEffect(() => {
    if (multimodal.onUserTextReceived && Array.isArray(multimodal.onUserTextReceived)) {
      const userText = multimodal.onUserTextReceived.join("");
      if (userText.trim()) {
        storeChatHistory(
          chatSessionId,
          userText,
          "voice_transcript",
          true,
          null,
          null,
          userId
        );
      }
    }
  }, [multimodal.onUserTextReceived, userId, chatSessionId]);

  const toggleVoicePopup = () => setShowVoicePopup(prev => !prev);

  // Start voice session (uses OpenAI Realtime via multimodal client)
  const startVoice = useCallback(async () => {
    try {
      if (!multimodal.isConnected) {
        await multimodal.connect();
      }
      const success = await multimodal.startRecording();
      if (success) {
        setVoiceAnimationState('listening');
        setVoiceStatus('listening');
      }
      return success;
    } catch (error) {
      console.error('[VoiceContext] Failed to start voice:', error);
      return false;
    }
  }, [multimodal]);

  // Stop voice session
  const stopVoice = useCallback(() => {
    multimodal.stopRecording();
    setVoiceAnimationState('idle');
    setVoiceStatus('idle');
  }, [multimodal]);

  // Initialize webcam for video voice mode
  const initializeWebcam = useCallback(async (videoElement) => {
    return await multimodal.initializeWebcam(videoElement);
  }, [multimodal]);

  // Initialize screen share
  const initializeScreenShare = useCallback(async (videoElement) => {
    return await multimodal.initializeScreenShare(videoElement);
  }, [multimodal]);

  // Stop video
  const stopVideo = useCallback(() => {
    multimodal.stopVideo();
  }, [multimodal]);

  const value = {
    // Multimodal client state
    ...multimodal,
    
    // Common state
    showVoicePopup,
    setShowVoicePopup,
    toggleVoicePopup,
    voiceAnimationState,
    voicePosition,
    setVoicePosition,
    voiceStatus,
    
    // Voice actions
    startVoice,
    stopVoice,
    initializeWebcam,
    initializeScreenShare,
    stopVideo,
    
    // Legacy compatibility - these are no longer used but kept for backwards compatibility
    geminiLiveEnabled: false,
    geminiLiveMode: 'disabled',
    geminiTimeRemaining: 0,
    geminiSessionWarning: null,
    geminiStatus: 'idle',
    connectGeminiLive: () => Promise.resolve(false),
    startGeminiAudio: () => Promise.resolve(false),
    stopGeminiAudio: () => {},
    initializeGeminiWebcam: () => Promise.resolve(false),
    initializeGeminiScreenShare: () => Promise.resolve(false),
    stopGeminiVideo: () => {},
    triggerBargeIn: () => {},
    captureAndUploadScreenshot: () => Promise.resolve(null),
    disconnectGeminiLive: () => {},
    sendGeminiText: () => false,
  };

  return (
    <VoiceContext.Provider value={value}>
      {children}
    </VoiceContext.Provider>
  );
};

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
};
